import React from 'react'

import { FormattedMessage, InjectedIntlProps, injectIntl } from 'react-intl'
import { useSelector } from 'react-redux'

import { CardIcon, CardLabel } from '@components/UI/Card/Card'
import { MediaThumbnail } from '@components/UI/Media'
import { AspectRatios, SocialSubmissionTypes } from '@enums'
import { IApplicationState } from '@store'
import {
  selectActiveSubmissionMediaObject,
  selectCarouselLicensedMediaObjectThumbnail,
  selectMediaObjectStatusLabel,
  selectMediaThumbnail,
  selectSocialSubmissionType,
  selectSubmission,
} from '@store/submissions'
import { Container } from '@tribegroup'
import { getSubmissionFlags } from '@utils/getSubmissionFlags'
import { SubmissionCardTextPreview } from '../TextPreview/TextPreview'

interface ISubmissionCardThumbnailProps extends InjectedIntlProps {
  submissionId: number
}

export const getThumbnailIcon = (
  flags: ReturnType<typeof getSubmissionFlags>,
  socialSubmissionType: SocialSubmissionTypes,
) => {
  const {
    isTiktok,
    isReel,
    isText,
    isPin,
    isStory,
    isCarousel,
    isStandard,
    isMediaTypeVideo,
    isShorts,
    isSnap,
  } = flags

  if (isTiktok) {
    return 'tiktok'
  }

  if (isReel) {
    return 'reels'
  }

  if (isText) {
    return 'text-post'
  }

  if (isShorts) {
    return 'shorts'
  }

  if (isSnap) {
    return 'snap'
  }

  const isMultiframe = isStory || isCarousel

  const showVideo = isMediaTypeVideo && (isStandard || isPin)

  if (showVideo) {
    return 'video'
  }

  if (isPin) {
    return 'idea-pin'
  }

  if (isMultiframe) {
    return socialSubmissionType
  }
  return undefined
}

const SubmissionCardThumbnail: React.FC<ISubmissionCardThumbnailProps> = ({
  intl,
  submissionId,
}) => {
  const {
    flags,
    statusLabel,
    licensedMediaThumbnail,
    mediaThumbnail,
    socialSubmissionType,
  } = useSelector((state: IApplicationState) => {
    const { id: mediaObjectId } = selectActiveSubmissionMediaObject(state, submissionId)

    const statusLabel = selectMediaObjectStatusLabel(state, submissionId, mediaObjectId)
    const submission = selectSubmission(state, submissionId)
    return {
      flags: getSubmissionFlags(submission),
      statusLabel,
      mediaThumbnail: selectMediaThumbnail(state, submissionId),
      licensedMediaThumbnail: selectCarouselLicensedMediaObjectThumbnail(state, submissionId),
      socialSubmissionType: selectSocialSubmissionType(state, submissionId),
    }
  })

  const icon = getThumbnailIcon(flags, socialSubmissionType)
  const iconTooltip = icon && intl.formatMessage({ id: `submission.card.thumbnail.${icon}` })

  return (
    <Container relative>
      {flags.isText ? (
        <SubmissionCardTextPreview submissionId={submissionId} />
      ) : (
        <MediaThumbnail
          src={licensedMediaThumbnail || mediaThumbnail}
          aspectRatio={AspectRatios.ONE_BY_ONE}
        />
      )}
      {statusLabel && (
        <CardLabel>
          <FormattedMessage id={`submission.card.label.${statusLabel}`} />
        </CardLabel>
      )}
      {icon && <CardIcon icon={icon} iconTooltip={iconTooltip} />}
    </Container>
  )
}

export { SubmissionCardThumbnail }
export default injectIntl(SubmissionCardThumbnail)
